/**
 * RecaptchaService - Google reCAPTCHA v3 integration for form protection
 *
 * Boundary: Does not handle server-side verification - returns token only
 * External dependencies:
 * - Script: https://www.google.com/recaptcha/api.js (dynamically loaded)
 * - Environment: recaptcha.siteKey configuration
 */
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LoggingService } from '@core/services/logging.service';
import { ApiConfigurationService } from '@core/services/api-configuration.service';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

/**
 * Manages reCAPTCHA v3 script loading and token generation for bot protection.
 *
 * Dependencies:
 * - LoggingService: Error logging and debug output
 *
 * Side effects:
 * - DOM: Injects reCAPTCHA script tag into document.head
 * - window.grecaptcha: Accesses global reCAPTCHA object
 * - Network: Loads script from google.com or recaptcha.net (fallback)
 *
 * @remarks
 * This service loads the reCAPTCHA v3 script dynamically and provides methods to execute
 * reCAPTCHA verification for different actions (login, signup, etc.)
 *
 * Usage:
 * 1. Ensure reCAPTCHA script is loaded in index.html OR use this service's load method
 * 2. Call execute() method with an action name to get a token
 * 3. Send the token to your backend for verification
 *
 * Security:
 * - Never trust the client-side reCAPTCHA result alone
 * - Always verify the token on the server-side using Google's API
 * - Tokens expire after 2 minutes
 */
@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private scriptLoaded = false;
  private loadingPromise: Promise<void> | null = null;
  private readyPromise: Promise<void> | null = null;
  private lastExecuteAt = 0;

  private loggingService = inject(LoggingService);
  private apiConfigurationService = inject(ApiConfigurationService);
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  /** Site key resolved from runtime configuration (see resolveSiteKey). */
  private resolvedSiteKey = '';

  /**
   * Dynamically load the reCAPTCHA v3 script if not already loaded
   * This is called automatically by execute() but can be called manually for preloading
   *
   * @returns Promise that resolves when the script is loaded
   */
  private loadScript(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      // Check if script already exists in DOM (api.js from google or recaptcha.net)
      const existingScript = document.querySelector(
        'script[src*="www.google.com/recaptcha/api.js"], script[src*="www.recaptcha.net/recaptcha/api.js"]'
      );

      const siteKey = this.getSiteKey();
      if (!siteKey) {
        this.loadingPromise = null;
        reject(new Error('Missing reCAPTCHA site key'));
        return;
      }

      // Create and load the script
      const appendScript = (src: string) => {
        const script = document.createElement('script');
        script.id = 'sn-recaptcha-script';
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.scriptLoaded = true;
          this.loadingPromise = null;
          resolve();
        };
        script.onerror = () => {
          // Try fallback domain if google.com blocked
          if (src === `https://www.google.com/recaptcha/api.js?render=${siteKey}`) {
            appendScript(`https://www.recaptcha.net/recaptcha/api.js?render=${siteKey}`);
          } else {
            this.loadingPromise = null;
            reject(new Error('Failed to load reCAPTCHA script'));
          }
        };
        document.head.appendChild(script);
      };

      if (existingScript) {
        this.scriptLoaded = true;
        this.loadingPromise = null;
        resolve();
        return;
      }

      appendScript(`https://www.google.com/recaptcha/api.js?render=${siteKey}`);
    });

    return this.loadingPromise;
  }

  /**
   * Ensure `grecaptcha.ready` has fired once before attempting execution.
   *
   * This method resolves when the reCAPTCHA library signals readiness.
   * If grecaptcha is present but not fully initialized, it polls for up to
   * 5 seconds before rejecting with an initialization error.
   *
   * @returns Promise that resolves when `grecaptcha.ready` fires, or rejects
   *          if initialization does not complete within the timeout window.
   */
  private ensureReady(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = new Promise<void>((resolve, reject) => {
      try {
        if (!window.grecaptcha?.ready) {
          // In rare cases grecaptcha is present but not fully initialized; poll briefly
          const start = Date.now();
          const check = () => {
            if (window.grecaptcha?.ready) {
              window.grecaptcha.ready(() => resolve());
              return;
            }
            if (Date.now() - start > 5000) {
              reject(new Error('reCAPTCHA not initialized'));
              return;
            }
            setTimeout(check, 50);
          };
          check();
        } else {
          window.grecaptcha.ready(() => resolve());
        }
      } catch (e) {
        reject(e as any);
      }
    });
    return this.readyPromise;
  }

  /**
   * Executes reCAPTCHA verification and returns token for backend validation.
   *
   * @param action - Action identifier for reCAPTCHA analytics (e.g., 'login', 'signup')
   * @returns Promise resolving to reCAPTCHA token string
   * - Observable semantics: N/A (Promise-based)
   * - HTTP errors: N/A (client-side only)
   * - State touched: Updates lastExecuteAt timestamp to prevent rapid-fire requests
   * - Idempotency: Safe to call multiple times; implements retry logic (3 attempts)
   *
   * @throws Error if reCAPTCHA not initialized, site key missing, or token generation fails
   *
   * @example
   * const token = await recaptchaService.execute('login');
   * // Send token to backend for verification
   */
  async execute(action: string = 'submit'): Promise<string> {
    try {
      const siteKey = await this.resolveSiteKey();
      if (!siteKey) {
        // In development, bypass for local auth flows if key missing
        if (!environment.production) return 'dev-bypass';
        throw new Error('Missing reCAPTCHA site key');
      }

      // Ensure script is loaded and ready
      await this.loadScript();
      await this.ensureReady();

      // Wait for grecaptcha to be ready
      if (!window.grecaptcha || !window.grecaptcha.ready) {
        throw new Error('reCAPTCHA not initialized');
      }

      // Prevent rapid back-to-back executes which sometimes yield empty tokens
      const sinceLast = Date.now() - this.lastExecuteAt;
      if (sinceLast < 100) {
        await new Promise((r) => setTimeout(r, 120 - sinceLast));
      }

      const attempt = async (): Promise<string> => {
        const token: string = await window.grecaptcha.execute(siteKey, { action });
        if (!token || token.trim().length === 0) {
          throw new Error('reCAPTCHA token is empty');
        }
        return token;
      };

      let lastErr: any = null;
      for (let i = 0; i < 3; i++) {
        try {
          const token = await attempt();
          this.lastExecuteAt = Date.now();
          return token;
        } catch (e: any) {
          lastErr = e;
          // Small backoff then retry
          await new Promise((r) => setTimeout(r, 150 * (i + 1)));
        }
      }

      // Final fallback in development to unblock local testing
      if (!environment.production) {
        this.loggingService.logWarning(
          '⚠️ reCAPTCHA dev bypass in effect: returning mock token. Verify real flow before release.',
        );
        return 'dev-bypass';
      }
      throw lastErr || new Error('reCAPTCHA execution failed');
    } catch (error: any) {
      console.error('reCAPTCHA error:', error);
      throw new Error(`reCAPTCHA verification failed: ${error.message || error}`);
    }
  }

  /**
   * Checks if reCAPTCHA is loaded and ready for execution.
   *
   * @returns True if reCAPTCHA script loaded and ready callback available
   * - Observable semantics: N/A (synchronous check)
   * - HTTP errors: N/A
   * - State touched: None (read-only)
   * - Idempotency: Safe to call multiple times
   */
  isReady(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.scriptLoaded && !!window.grecaptcha?.ready;
  }

  /**
   * Resolves the site key. Production uses the single production key served
   * by `/api/configuration` (app setting `RECAPTCHA_SITE_KEY`), shared with
   * scanninja.ai; the build no longer embeds one (#870). Other builds fall back
   * to `environment.recaptcha.siteKey` when the API serves none.
   *
   * @returns The site key, or '' when none is configured.
   */
  async resolveSiteKey(): Promise<string> {
    if (this.resolvedSiteKey) {
      return this.resolvedSiteKey;
    }
    let key = (await this.apiConfigurationService.getRecaptchaSiteKey()).trim();
    if (!key && !environment.production) {
      key = environment.recaptcha.siteKey;
    }
    this.resolvedSiteKey = key;
    return key;
  }

  /**
   * True when a site key is configured, so verification is enforced.
   *
   * @returns Whether reCAPTCHA is configured for this deployment.
   */
  async isConfigured(): Promise<boolean> {
    return !!(await this.resolveSiteKey());
  }

  /**
   * Verifies a token server-side (`POST /api/recaptcha/verify`). The API checks
   * the token with Google, including the action, score and hostname (#870).
   * Any failure, including a server that cannot verify, emits false.
   *
   * @param token Token from execute().
   * @param action Action the token was minted for.
   * @returns Emits whether the token passed verification.
   */
  verify(token: string, action: string): Observable<boolean> {
    // authLevel "function": the Functions key comes from runtime configuration,
    // as in PaymentService (no environment defines app_api_token).
    return from(
      this.apiConfigurationService.getApiFunctionToken().catch(() => ({ value: '' })),
    ).pipe(
      switchMap(({ value: functionKey }) =>
        this.http.post<{ success: boolean; valid?: boolean }>(
          `${environment.app_api_url}/api/recaptcha/verify`,
          { token, action },
          functionKey
            ? { headers: new HttpHeaders({ 'x-functions-key': functionKey }) }
            : {},
        ),
      ),
        map((response) => response?.success === true && response.valid === true),
        catchError(() => of(false)),
      );
  }

  /**
   * The resolved site key (empty until resolveSiteKey has run).
   *
   * @returns Site key string, or ''.
   */
  getSiteKey(): string {
    return this.resolvedSiteKey;
  }
}
